import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Alert,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Search from "@mui/icons-material/Search";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ChartCard from "@/components/data-visuals/ChartCard";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import { MEDIDROP_COLORS } from "@/config/colors";
import AdminMediDropStatCard from "@/components/admin/AdminMediDropStatCard";
import { getChemistName } from "@/utils/textUtils";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import {
  ADMIN_TOP_CARD_HEIGHT,
  adminDateRangePickerWideNestedSx,
  mediDropDataGridSx,
} from "@/utils/adminTableStyles";

const MEDIDROP_ORANGE = MEDIDROP_COLORS.ORANGE;

const mediDropDateRangeSx = {
  borderRadius: "8px",
  py: 1.5,
  px: 1.5,
  height: ADMIN_TOP_CARD_HEIGHT,
  minHeight: ADMIN_TOP_CARD_HEIGHT,
  maxHeight: ADMIN_TOP_CARD_HEIGHT,
  boxSizing: "border-box",
  width: "100%",
  minWidth: 0,
  backgroundColor: MEDIDROP_COLORS.ORANGE_DARK,
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  "& .text-black": { color: "white !important" },
  ...adminDateRangePickerWideNestedSx,
};

/** Fill missing days so the chart has a continuous series. */
const preprocessDeliverySeries = (rows, dateStart, dateEnd) => {
  if (!rows || !Array.isArray(rows)) return [];
  const start = new Date(dateStart);
  const end = new Date(dateEnd);
  const map = new Map();
  rows.forEach((r) => {
    const key = r.Day || r.date;
    if (key) map.set(key, Number(r.Count ?? r.count ?? 0));
  });
  const out = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().split("T")[0];
    out.push({ Day: key, Count: map.get(key) || 0 });
  }
  return out;
};

const AdminMediDropPage = ({ isManager = false }) => {
  const navigate = useNavigate();
  const chemistBasePath = isManager ? "/manager-functions/chemist" : "/admin-functions/chemist";
  const [data, setData] = useState(null);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [tableFilter, setTableFilter] = useState("");
  const [chartOpen, setChartOpen] = useState(false);

  useEffect(() => {
    const today = new Date();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(today.getDate() - 28);
    setDateStart(fourWeeksAgo.toISOString().split("T")[0]);
    setDateEnd(today.toISOString().split("T")[0]);
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await phappsRequest(
        isManager ? API_PATHS.MANAGER_FUNCTIONS_MEDIDROP : API_PATHS.ADMIN_FUNCTIONS_MEDIDROP,
        "GET",
        null,
        { DateStart: dateStart, DateEnd: dateEnd }
      );
      setData(res);
      setFetchError(null);
    } catch (e) {
      console.error(e);
      setData(null);
      setFetchError(getAdminApiErrorMessage(e, "Failed to load MediDrop data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateStart && dateEnd) load();
  }, [dateStart, dateEnd]);

  const summary = data?.Summary || {};
  const totalDeliveries = Number(summary.TotalDeliveries ?? 0);
  const avgPerDay = Number(summary.AvgDeliveriesPerDay ?? 0);
  const activeChemists = Number(summary.ActiveChemistCount ?? 0);
  const totalDriverCount = Number(summary.TotalDriverCount ?? 0);
  const topChem = summary.HighestDeliveryChemist;

  const deliverySeries = useMemo(
    () => preprocessDeliverySeries(data?.DeliveriesPerDay || [], dateStart, dateEnd),
    [data, dateStart, dateEnd]
  );

  const chemRows = data?.ChemistTable || [];
  const gridRows = chemRows.map((row, i) => ({
    id: row.ChemistUUID || `r-${i}`,
    chemistUUID: row.ChemistUUID ?? "",
    chemistName: row.ChemistName,
    totalDeliveries: row.TotalDeliveries ?? 0,
    draftCount: row.DraftCount ?? 0,
    driverCount: row.DriverCount ?? 0,
    latestDelivery: row.LatestDelivery ?? null,
  }));

  const filteredRows = useMemo(() => {
    const q = tableFilter.trim().toLowerCase();
    if (!q) return gridRows;
    return gridRows.filter((r) =>
      String(getChemistName(r.chemistName) || "").toLowerCase().includes(q)
    );
  }, [gridRows, tableFilter]);

  const columns = [
    {
      field: "chemistName",
      headerName: "Chemist Name",
      flex: 1,
      minWidth: 200,
      renderCell: (p) => (
        <Typography
          variant="body2"
          onClick={() => p.row.chemistUUID && navigate(`${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`, { state: { tab: 5 } })}
          sx={{ cursor: p.row.chemistUUID ? "pointer" : "default", fontWeight: 500, "&:hover": { textDecoration: p.row.chemistUUID ? "underline" : "none" } }}
        >
          {getChemistName(p.value)}
        </Typography>
      ),
    },
    {
      field: "totalDeliveries",
      headerName: "Deliveries",
      width: 110,
      type: "number",
    },
    {
      field: "draftCount",
      headerName: "Existing Drafts",
      width: 140,
      type: "number",
    },
    {
      field: "driverCount",
      headerName: "Drivers",
      width: 90,
      type: "number",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "latestDelivery",
      headerName: "Latest delivery",
      width: 140,
      renderCell: (p) => p.value ?? "—",
    },
    {
      field: "actions",
      headerName: "Open",
      width: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Button
          size="small"
          variant="contained"
          disabled={!p.row.chemistUUID}
          onClick={() =>
            navigate(
              `${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`,
              { state: { tab: 5 } }
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: MEDIDROP_ORANGE,
            minWidth: 60,
            "&:hover": { backgroundColor: "#d4613c" },
          }}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        <Typography variant="h5" fontWeight="bold" sx={{ color: MEDIDROP_ORANGE }}>
          Admin MediDrop overview
        </Typography>

        {fetchError ? <Alert severity="warning">{fetchError}</Alert> : null}

        {loading && !data ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div
                className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
                style={{ borderColor: MEDIDROP_ORANGE }}
              />
              <p className="text-gray-600">Loading MediDrop data…</p>
            </div>
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
              <Box sx={mediDropDateRangeSx}>
                <SFDateRangeSelector
                  adminToolbar
                  value={[new Date(dateStart), new Date(dateEnd)]}
                  onChange={(range) => {
                    setDateStart(range[0]?.toISOString().split("T")[0]);
                    setDateEnd(range[1]?.toISOString().split("T")[0]);
                  }}
                />
              </Box>

              <div className="min-w-0">
                <AdminMediDropStatCard
                  title="Total deliveries"
                  value={Number(totalDeliveries).toLocaleString()}
                  Icon={LocalShippingOutlinedIcon}
                />
              </div>

              <div className="min-w-0">
                <AdminMediDropStatCard
                  title="Avg / day"
                  value={Number(avgPerDay).toFixed(1)}
                  Icon={TrendingUpIcon}
                />
              </div>

              <div className="min-w-0">
                <AdminMediDropStatCard
                  title="Total drivers"
                  value={Number(totalDriverCount).toLocaleString()}
                  Icon={DirectionsCarOutlinedIcon}
                />
              </div>

              <div className="min-w-0">
                <AdminMediDropStatCard
                  title="Most deliveries"
                  value={
                    topChem?.ChemistName != null
                      ? getChemistName(topChem.ChemistName)
                      : "—"
                  }
                  Icon={EmojiEventsOutlinedIcon}
                  textValue
                />
              </div>
            </section>

            <Card
              variant="outlined"
              sx={{
                minWidth: 0,
                borderColor: MEDIDROP_ORANGE,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography fontWeight={700} sx={{ color: MEDIDROP_ORANGE }}>
                    Chemists ({activeChemists})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setChartOpen(true)}
                      sx={{
                        textTransform: "none",
                        borderColor: MEDIDROP_ORANGE,
                        color: MEDIDROP_ORANGE,
                        fontWeight: 600,
                      }}
                    >
                      Deliveries over time
                    </Button>
                    <TextField
                      size="small"
                      placeholder="Filter by chemist name"
                      value={tableFilter}
                      onChange={(e) => setTableFilter(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 260 }}
                    />
                  </Box>
                </Box>
                <Divider />
                <div style={{ height: 520, width: "100%", padding: "0 8px 16px" }}>
                  <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    loading={loading}
                    pageSizeOptions={[10, 20, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 20 } },
                      sorting: { sortModel: [{ field: "totalDeliveries", sort: "desc" }] },
                    }}
                    disableRowSelectionOnClick
                    sx={mediDropDataGridSx}
                  />
                </div>
              </CardContent>
            </Card>

            <Dialog
              open={chartOpen}
              onClose={() => setChartOpen(false)}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle sx={{ color: MEDIDROP_ORANGE, fontWeight: 700 }}>
                Deliveries over time
              </DialogTitle>
              <DialogContent>
                <Box sx={{ "& > div": { mt: 0 }, pt: 1 }}>
                  <ChartCard
                    title=""
                    hidePieChart
                    mainChartMinHeight={360}
                    data={{ deliveries: deliverySeries }}
                    primaryXAxis={{
                      valueType: "DateTime",
                      labelFormat: "dd/MM",
                      intervalType: "Days",
                    }}
                    primaryYAxis={{
                      title: "Deliveries",
                      minimum: 0,
                      labelFormat: "{value}",
                    }}
                    borderColor={MEDIDROP_ORANGE}
                    series={[
                      {
                        dataSource: deliverySeries,
                        xName: "Day",
                        yName: "Count",
                        type: "Line",
                        name: "Deliveries",
                        width: 2,
                        fill: MEDIDROP_ORANGE,
                        marker: { visible: true, width: 8, height: 8 },
                      },
                    ]}
                    pieData={[]}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setChartOpen(false)} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminMediDropPage;
